import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { locate } from "@/lib/radioSchedule";
import { stopAmbient } from "@/lib/ambient";

export const CHANNELS = ["all", "silence", "liberte", "doute", "nature"];

const RadioCtx = createContext(null);
export const useRadio = () => useContext(RadioCtx);

export function RadioProvider({ children }) {
  const [manifest, setManifest] = useState(null);
  const [channels, setChannels] = useState(null);
  const [channel, setChannelState] = useState(() => {
    const c = localStorage.getItem("coccon_radio_channel");
    return CHANNELS.includes(c) ? c : "all";
  });
  const [now, setNow] = useState(Date.now() / 1000);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);
  const wantRef = useRef(false);
  const skewRef = useRef(0);
  const loadedRef = useRef(false);

  const ensureLoaded = useCallback(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    fetch("/derive/radio/manifest.json", { cache: "no-store" }).then((r) => r.json()).then(setManifest).catch(() => {});
    fetch("/derive/radio/channels.json", { cache: "no-store" }).then((r) => r.json())
      .then((stat) => api.get("/radio/channels").then(({ data }) => setChannels(data.channels || stat)).catch(() => setChannels(stat)))
      .catch(() => setChannels({}));
    const t0 = Date.now();
    api.get("/radio/clock").then(({ data }) => {
      skewRef.current = (data.now_ms + (Date.now() - t0) / 2 - Date.now()) / 1000;
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => clearInterval(id);
  }, []);

  const programFor = useCallback((ch) => {
    if (!manifest) return null;
    if (ch === "all" || !channels || !channels[ch]?.length) return manifest;
    const set = new Set(channels[ch]);
    const items = manifest.items.filter((it) => set.has(it.slug));
    return items.length ? { jingle: manifest.jingle, items } : manifest;
  }, [manifest, channels]);

  const program = useMemo(() => programFor(channel), [programFor, channel]);
  const liveNow = () => Date.now() / 1000 + skewRef.current;

  const tune = useCallback((prog) => {
    const a = audioRef.current;
    const p = prog || program;
    if (!a || !p) return;
    const pos = locate(p, liveNow());
    const src = pos.kind === "jingle" ? "/derive/radio/jingle.mp3" : `/derive/${p.items[pos.idx].slug}.mp3`;
    setLoading(true);
    stopAmbient();
    const start = () => {
      a.currentTime = locate(p, liveNow()).offset;
      a.play().then(() => { setPlaying(true); setLoading(false); })
        .catch((err) => {
          if (err?.name === "AbortError" && wantRef.current) { setTimeout(() => tune(), 300); return; }
          setLoading(false); setPlaying(false); wantRef.current = false;
        });
    };
    if (a.getAttribute("src") === src && a.readyState >= 1) { start(); return; }
    a.setAttribute("src", src);
    a.addEventListener("loadedmetadata", start, { once: true });
    a.load();
  }, [program]);

  useEffect(() => {
    const a = audioRef.current;
    if (!wantRef.current || !program || !a || a.paused) return;
    if (Math.abs(a.currentTime - locate(program, now + skewRef.current).offset) > 4) tune();
  }, [now, program, tune]);

  useEffect(() => {
    if (wantRef.current && program && audioRef.current?.paused && !playing) tune();
  }, [program]); // eslint-disable-line react-hooks/exhaustive-deps

  const play = () => { wantRef.current = true; if (!program) { ensureLoaded(); setLoading(true); return; } tune(); };
  const pause = () => { wantRef.current = false; audioRef.current?.pause(); setPlaying(false); };
  const toggle = () => (playing ? pause() : play());
  const setChannel = (ch) => {
    setChannelState(ch);
    localStorage.setItem("coccon_radio_channel", ch);
    if (wantRef.current) tune(programFor(ch));
  };
  const toggleMute = () => {
    const m = !muted; setMuted(m);
    if (audioRef.current) audioRef.current.muted = m;
  };

  const pos = program ? locate(program, now + skewRef.current) : null;
  const value = {
    ensureLoaded, manifest, program, channels, channel, setChannel, pos, now: now + skewRef.current,
    playing, loading, muted, play, pause, toggle, toggleMute,
    currentSlug: pos ? program.items[pos.idx].slug : null,
    nextSlug: pos ? program.items[pos.kind === "jingle" ? pos.idx : pos.nextIdx].slug : null,
  };

  return (
    <RadioCtx.Provider value={value}>
      {children}
      <audio ref={audioRef} onEnded={() => { if (wantRef.current) tune(); }} preload="none" data-testid="radio-audio" />
    </RadioCtx.Provider>
  );
}
